import { TermsDocumentPublic } from '../../../types';

interface TermsDocumentPanelProps {
  doc: TermsDocumentPublic;
  activeIndex: number;
  totalDocs: number;
  isAgreed: boolean;
  isLast: boolean;
  isArabic: boolean;
  isDownloading: boolean;
  onAgree: () => void;
  onContinue: () => void;
  onDownload: () => void;
  labels: {
    docEyebrow: (current: number, total: number) => string;
    download: string;
    downloadInProgress: string;
    agreeAndNextOf: (next: number, total: number) => string;
    agreeAndReview: string;
    agreeAndContinue: string;
    alreadyAgreed: string;
    continueNext: string;
    goToReview: string;
  };
}

export default function TermsDocumentPanel({
  doc,
  activeIndex,
  totalDocs,
  isAgreed,
  isLast,
  isArabic,
  isDownloading,
  onAgree,
  onContinue,
  onDownload,
  labels,
}: TermsDocumentPanelProps) {
  const title = isArabic && doc.titleAr ? doc.titleAr : doc.title;
  const body = isArabic && doc.bodyAr ? doc.bodyAr : doc.body;
  const isSingleDoc = totalDocs === 1;

  const agreeLabel = isSingleDoc
    ? labels.agreeAndContinue
    : isLast
      ? labels.agreeAndReview
      : labels.agreeAndNextOf(activeIndex + 2, totalDocs);

  return (
    <div className="terms-doc-content" key={doc.id}>
      <div className="terms-doc-header">
        <div className="terms-doc-meta">
          <span className="terms-doc-eyebrow">
            {labels.docEyebrow(activeIndex + 1, totalDocs)}
          </span>
          <h3 className="terms-doc-title">{title}</h3>
        </div>
        <button
          type="button"
          className="terms-doc-download-btn"
          onClick={onDownload}
          disabled={isDownloading}
          aria-label={labels.download}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{isDownloading ? labels.downloadInProgress : labels.download}</span>
        </button>
      </div>

      <div
        className="onboarding-policy-text terms-doc-body"
        dir={isArabic ? 'rtl' : 'ltr'}
        dangerouslySetInnerHTML={{ __html: body }}
      />

      <div className="terms-doc-action">
        {isAgreed ? (
          <div className="terms-already-agreed">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#16a34a"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>{labels.alreadyAgreed}</span>
            <button
              type="button"
              className="terms-action-link"
              onClick={onContinue}
            >
              {isLast ? labels.goToReview : labels.continueNext}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="terms-agree-btn"
            onClick={onAgree}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
            {agreeLabel}
          </button>
        )}
      </div>
    </div>
  );
}
